import { type ClassValue, clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount)
}

export function formatNumber(num: number): string {
  return new Intl.NumberFormat('en-US').format(num)
}

export function formatCompactNumber(num: number): string {
  return new Intl.NumberFormat('en-US', { notation: 'compact' }).format(num)
}

export function formatDate(date: Date | string): string {
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(new Date(date))
}

export function formatRelativeTime(date: Date | string): string {
  const now = new Date()
  const then = new Date(date)
  if (isNaN(then.getTime())) return 'Unknown'
  const diffMs = now.getTime() - then.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffMins < 1) return 'just now'
  if (diffMins < 60) return `${diffMins}m ago`
  if (diffHours < 24) return `${diffHours}h ago`
  if (diffDays < 7) return `${diffDays}d ago`
  return formatDate(date)
}

export function getInitials(name: string): string {
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)
}

export function generateId(): string {
  return Math.random().toString(36).slice(2, 11)
}

// ── Token Cost Constants ──

export const TOKEN_COSTS = {
  chatSimple: 500,
  chatComplex: 2500,
  scoreLead: 100,
  scoreSingle: 200,
  skipTrace: 1500,
  propertyEnrich: 800,
  comps: 3000,
  campaign: 5000,
  dealAnalysis: 3000,
  autoPilotRun: 25000,
  insight: 2000,
  exportReport: 1000,
  propertyLookup: 500,
} as const

export function formatTokens(count: number): string {
  if (count >= 1_000_000) return `${(count / 1_000_000).toFixed(1)}M`
  if (count >= 10_000) return `${(count / 1_000).toFixed(0)}K`
  if (count >= 1_000) return `${(count / 1_000).toFixed(1)}K`
  return count.toLocaleString()
}

// ═══════════════════════════════════════════════════════════════
// URL Sanitization
// ═══════════════════════════════════════════════════════════════
//
// SECURITY: Any untrusted URL string that will be written to an
// `href`, `src`, or similar URL-valued DOM attribute MUST be passed
// through `sanitizeUrl` first. This includes:
//
//   - Links extracted from markdown content in chat messages
//     (agent-generated content is NOT trusted input)
//   - Images, audio, and video sources from any user-supplied content
//   - Redirect targets built from query strings or route params
//
// `javascript:`, `data:`, `vbscript:`, and `file:` protocols can
// execute arbitrary script when clicked, leading to stored/reflected
// XSS. Never bypass this function with a raw URL interpolation.

const ALLOWED_URL_PROTOCOLS = new Set(['http:', 'https:', 'mailto:'])

/**
 * Return a safe URL string, or `null` if the URL uses a disallowed
 * protocol. Callers should render plain text (or a disabled link) when
 * the return value is `null`.
 *
 * Allowed protocols: `http`, `https`, `mailto`. Relative URLs, fragment
 * links, protocol-relative URLs, and bare paths are preserved.
 *
 * Control characters (NUL, tab, newline, etc.) are stripped before the
 * protocol check because browsers ignore them when parsing URLs — they
 * are a common bypass for naive prefix checks
 * (e.g. `java\tscript:alert(1)` parses as `javascript:`).
 */
export function sanitizeUrl(url: string | null | undefined): string | null {
  if (url == null) return null

  // Strip control chars anywhere in the string (browsers ignore these
  // during URL parsing, so attackers use them to smuggle protocols).
  // eslint-disable-next-line no-control-regex -- intentional: stripping control chars is the whole point
  const cleaned = String(url).replace(/[\u0000-\u001f\u007f]/g, '').trim()
  if (!cleaned) return null

  // Relative / fragment / query / path URLs are safe.
  if (/^(?:[#/?]|\.\.?\/)/.test(cleaned)) return cleaned

  // Protocol-relative (`//host/path`) resolves to the current scheme.
  if (cleaned.startsWith('//')) return cleaned

  // If an explicit protocol is declared, it must be on the allowlist.
  const protoMatch = cleaned.match(/^([a-z][a-z0-9+.-]*):/i)
  if (protoMatch) {
    const protocol = `${protoMatch[1]}:`.toLowerCase()
    if (!ALLOWED_URL_PROTOCOLS.has(protocol)) return null
    return cleaned
  }

  // No protocol and not a path — bare text like "example.com". Safe.
  return cleaned
}
